import { RegistrationSource, Source } from 'src/apollo/onlineOrdering'
import {
  isToastLocalSurfaceRequest,
  isToastOrderRequest
} from 'src/public/js/siteUtilities'

/**
 * Resolve the account `RegistrationSource` for a new account being created
 * from toast-sites-web. Driven by the URL the guest is registering from
 * plus the restaurant's online-ordering-only configuration. Existing-user
 * session plumbing (token refresh, logout) uses `getSessionSource` instead.
 *
 * Callers are responsible for resolving `isOnlineOrderingOnly` from the
 * active restaurant context and must only invoke this from surfaces that
 * allow account creation. Surfaces without a `RestaurantContextProvider`
 * ancestor (standalone-website pages, menu-only pages) don't support
 * account creation today; a future change that adds account creation to
 * such a surface must extend this function rather than defaulting
 * `isOnlineOrderingOnly` to `false`, which would silently mis-attribute
 * custom-domain OO-only registrations as `Websites`.
 *
 * Toast Local is matched on every TL surface, so guests redirected from
 * `/local` to toast.app keep the `LOCAL` attribution instead of falling
 * through to the custom-domain branch.
 *
 * See CO-1072 for the enum split from the aggregate `SITES` value.
 */
export const getRegistrationSource = (
  isOnlineOrderingOnly: boolean
): RegistrationSource => {
  if (isToastLocalSurfaceRequest()) {
    return RegistrationSource.Local
  }
  if (isToastOrderRequest()) {
    return RegistrationSource.OnlineOrdering
  }
  // Custom domain (or *.test.toast.site preview). OO-only sites are
  // branded OO; otherwise the site also has website pages bundled with OO.
  return isOnlineOrderingOnly
    ? RegistrationSource.BrandedOnlineOrdering
    : RegistrationSource.Websites
}

/**
 * Companion to {@link getRegistrationSource} returning the `Source` enum
 * used by passwordless login / confirm-code mutations. Same decision tree,
 * mapped to the `Source` vocabulary.
 */
export const getSource = (isOnlineOrderingOnly: boolean): Source => {
  if (isToastLocalSurfaceRequest()) {
    return Source.ToastLocal
  }
  if (isToastOrderRequest()) {
    return Source.ToastOnlineOrdering
  }
  return isOnlineOrderingOnly
    ? Source.ToastBrandedOnlineOrdering
    : Source.ToastWebsites
}

/**
 * Resolve the surface the guest is interacting with for session operations
 * on an existing account (token refresh, logout). Unlike
 * {@link getRegistrationSource}, this is not about attributing a new
 * account — it identifies the surface for an already-created session.
 *
 * URL-driven — no restaurant context is available, because the module-level
 * Apollo error-link caller in `createClient.ts` runs at module init. On
 * custom domains this still emits the deprecated aggregate
 * `Source.ToastSites`; resolving those to per-surface values
 * (`Websites`, `BrandedOnlineOrdering`, etc.) requires plumbing
 * `isOnlineOrderingOnly` through `createAuthRefreshClient` / `createClient`
 * and converting the error links into factories.
 */
export const getSessionSource = (): Source => {
  if (isToastLocalSurfaceRequest()) {
    return Source.ToastLocal
  }
  if (isToastOrderRequest()) {
    return Source.ToastOnlineOrdering
  }

  // TODO: COLA-2566 - replace this deprecated value with the correct value
  // from the execution context
  return Source.ToastSites
}
